import type { HubGame } from '@/types';

export interface LiveTableRow {
  gameId: string;
  tableName: string;
  minLimit: string;
  maxLimit: string;
  hours: string;
  language: string;
  releaseDate: string;
  location: string;
}

export interface LiveCopy {
  intro: string[];
  features: string[];
  tables: LiveTableRow[];
}

/**
 * Editorial sheet per live product. Limits, hours and studio location are
 * commercial data the catalog does not expose, so they live here until the
 * platform publishes them.
 */
const LIVE_COPY: Record<string, LiveCopy> = {
  'royal-roulette': {
    intro: [
      "RichyPlay's Royal Roulette table integrates exceptional features from our live studio, presenting an unmatched and luxurious feel.",
      'A beautifully designed studio welcomes players, and our attentive, professionally trained dealers are committed to providing a premium user experience.',
    ],
    features: [
      'Experienced and customer-oriented professionally trained dealers - increased betting time to 20 sec',
      'Special Royal table label in the lobby to indicate table status',
      'Luxury and premium studio feel',
      'Detailed bet history for the player',
      'Advanced bets validation',
    ],
    tables: [
      {
        gameId: '501',
        tableName: 'Royal Roulette',
        minLimit: '€1',
        maxLimit: '€20,000',
        hours: '24/7',
        language: 'English',
        releaseDate: '1 Sep 2026',
        location: 'RichyPlay Studio',
      },
    ],
  },
  'royal-blackjack': {
    intro: [
      "RichyPlay's Royal Blackjack table combines classic seven-seat play with the production quality of our flagship live studio.",
      'Unlimited seats through bet behind, native side bets and a premium studio set keep the table engaging for both new and high-value players.',
    ],
    features: [
      'Seven seats with unlimited bet behind capacity',
      'Perfect Pairs and 21+3 side bets',
      'Pre-decision and insurance support',
      'Detailed hand history for the player',
      'Advanced bets validation',
    ],
    tables: [
      {
        gameId: '502',
        tableName: 'Royal Blackjack',
        minLimit: '€5',
        maxLimit: '€10,000',
        hours: '24/7',
        language: 'English',
        releaseDate: '1 Sep 2026',
        location: 'RichyPlay Studio',
      },
    ],
  },
};

function copyKey(game: HubGame): string | null {
  const text = [game.id, game.title, game.details?.gameType, game.details?.tableName]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (/roulette/.test(text)) return 'royal-roulette';
  if (/blackjack/.test(text)) return 'royal-blackjack';
  return null;
}

/** Catalog values win over the editorial sheet whenever the platform has them. */
export function liveCopy(game: HubGame, locale: string): LiveCopy | null {
  if (game.category !== 'live') return null;
  const key = copyKey(game);
  if (!key) return null;

  const base = LIVE_COPY[key];
  const details = game.details ?? {};

  const releaseDate = details.releaseDate ? new Date(details.releaseDate) : null;
  const formattedDate =
    releaseDate && !Number.isNaN(releaseDate.getTime())
      ? releaseDate.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' })
      : null;

  return {
    ...base,
    tables: base.tables.map((row) => ({
      ...row,
      gameId: details.tableId ?? row.gameId,
      tableName: details.tableName ?? row.tableName,
      releaseDate: formattedDate ?? row.releaseDate,
    })),
  };
}
